/**
 * BrowserTypes - Type definitions for browser APIs that may not be fully typed
 * 
 * This file provides proper TypeScript definitions for browser APIs that are
 * commonly accessed with 'as any' casts, improving type safety.
 */

/**
 * Extended Window interface with optional garbage collection
 */
declare global {
  interface Window {
    gc?: () => void;
  }
}

/**
 * Extended Performance interface with memory information
 */
export interface PerformanceMemory {
  readonly usedJSHeapSize: number;
  readonly totalJSHeapSize: number;
  readonly jsHeapSizeLimit: number;
}

export interface ExtendedPerformance extends Performance {
  memory?: PerformanceMemory;
}

/**
 * Type guards for checking browser API availability
 */
export const BrowserAPI = {
  /**
   * Check if garbage collection is available
   */
  hasGarbageCollection(): boolean {
    return 'gc' in window && typeof window.gc === 'function';
  },

  /**
   * Check if performance memory API is available
   */
  hasPerformanceMemory(): boolean {
    return 'memory' in performance;
  },

  /**
   * Safely call garbage collection if available
   */
  callGarbageCollection(): void {
    if (this.hasGarbageCollection()) {
      window.gc!();
    }
  },

  /**
   * Safely get performance memory info
   */
  getPerformanceMemory(): PerformanceMemory | null {
    if (this.hasPerformanceMemory()) {
      return (performance as ExtendedPerformance).memory || null;
    }
    return null;
  }
};

/**
 * Operation type definitions for better type safety
 */
export interface TypedInsertOperation {
  type: 'insert';
  position: number;
  text: string;
  content: string;
  id: string;
  timestamp: Date;
  version: number;
  attorney: {
    id: string;
    name: string;
    email: string;
    barNumber: string;
    firmName: string;
    role: 'associate' | 'paralegal' | 'partner' | 'clerk';
    seniority: number;
  };
}

export interface TypedDeleteOperation {
  type: 'delete';
  position: number;
  start: number;
  end: number;
  deletedContent: string;
  id: string;
  timestamp: Date;
  version: number;
  attorney: {
    id: string;
    name: string;
    email: string;
    barNumber: string;
    firmName: string;
    role: 'associate' | 'paralegal' | 'partner' | 'clerk';
    seniority: number;
  };
}

export type TypedOperation = TypedInsertOperation | TypedDeleteOperation;

/**
 * Type guards for operations
 */
export const OperationTypeGuards = {
  isInsertOperation(op: any): op is TypedInsertOperation {
    return op && op.type === 'insert' && typeof op.position === 'number' && typeof op.text === 'string';
  },

  isDeleteOperation(op: any): op is TypedDeleteOperation {
    return op && op.type === 'delete' && typeof op.position === 'number' && typeof op.start === 'number' && typeof op.end === 'number';
  },

  hasPosition(op: any): op is { position: number } {
    return op && typeof op.position === 'number';
  }
};
